const DEFAULT_MIN_FONT_SIZE = 16;

function resolveFontSize(element) {
  return parseFloat(getComputedStyle(element).fontSize) || DEFAULT_MIN_FONT_SIZE;
}

function directBlockChildren(element) {
  return [...element.children].filter((child) => (
    child instanceof HTMLElement
      && (child.classList.contains('block') || getComputedStyle(child).display === 'block')
  ));
}

function resetFitStyle(element) {
  element.style.fontSize = '';
  element.style.whiteSpace = '';
}

function positiveNumber(value) {
  const number = parseFloat(value);
  return Number.isFinite(number) && number > 0 ? number : undefined;
}

function fitOptionsForHeading(heading) {
  return {
    availableWidth: heading.dataset.widthSource === 'parent' ? heading.parentElement?.clientWidth : undefined,
    minFontSize: positiveNumber(heading.dataset.minFontSize),
    maxFontSize: positiveNumber(heading.dataset.maxFontSize),
  };
}

export function fitElementToSingleLine(
  element,
  {
    availableWidth,
    maxFontSize,
    minFontSize = DEFAULT_MIN_FONT_SIZE,
  } = {}
) {
  if (!(element instanceof HTMLElement)) return;

  resetFitStyle(element);

  const resolvedMaxFontSize = maxFontSize ?? resolveFontSize(element);
  const resolvedMinFontSize = Math.min(minFontSize, resolvedMaxFontSize);

  element.style.whiteSpace = 'nowrap';
  element.style.fontSize = `${resolvedMaxFontSize}px`;

  const availableWidths = [availableWidth, element.clientWidth, element.parentElement?.clientWidth]
    .filter((width) => Number.isFinite(width) && width > 0);
  const resolvedAvailableWidth = availableWidths.length > 0 ? Math.min(...availableWidths) : 0;
  if (resolvedAvailableWidth <= 0 || element.scrollWidth <= resolvedAvailableWidth) {
    return;
  }

  let nextFontSize = Math.max(
    resolvedMinFontSize,
    Math.floor((resolvedMaxFontSize * resolvedAvailableWidth * 10) / element.scrollWidth) / 10
  );

  element.style.fontSize = `${nextFontSize}px`;

  while (element.scrollWidth > resolvedAvailableWidth && nextFontSize > resolvedMinFontSize) {
    nextFontSize = Math.max(resolvedMinFontSize, nextFontSize - 0.5);
    element.style.fontSize = `${nextFontSize}px`;
  }
}

export function fitHeadingToSingleLine(heading, { singleLine = false, ...options } = {}) {
  if (!(heading instanceof HTMLElement)) return;

  resetFitStyle(heading);

  if (singleLine) {
    fitElementToSingleLine(heading, options);
    return;
  }

  const lineElements = directBlockChildren(heading);
  if (lineElements.length > 0) {
    const availableWidth = heading.clientWidth || heading.parentElement?.clientWidth || 0;
    lineElements.forEach((lineElement) => fitElementToSingleLine(lineElement, { ...options, availableWidth }));
    return;
  }

  fitElementToSingleLine(heading, options);
}

export function initArchiveFitHeading(root, { headingSelector, lineSelector }) {
  const headings = [...root.querySelectorAll(`${headingSelector}, ${lineSelector}`)];
  if (headings.length === 0) return;

  const win = root.defaultView || window;
  const fitHeadings = () => {
    headings.forEach((heading) => fitHeadingToSingleLine(heading, {
      ...fitOptionsForHeading(heading),
      singleLine: heading.matches(lineSelector),
    }));
  };

  fitHeadings();
  win.addEventListener('resize', fitHeadings, { passive: true });
  win.document.fonts?.ready?.then(fitHeadings).catch(() => {});
}
